import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Users, CheckCircle2, Plus, Trash2, ArrowRight, Loader2, Droplets, Zap } from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const STEPS = ["Property", "Tenants", "Done"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addProperty, addTenant, finishOnboarding } = useData();
  const [step, setStep] = useState(0);
  const [property, setProperty] = useState({ name: "", address: "", waterEnabled: false, electricityEnabled: false });
  const [createdProperty, setCreatedProperty] = useState(null);
  const [tenantDraft, setTenantDraft] = useState(emptyTenant());
  const [stagedTenants, setStagedTenants] = useState([]);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const next = async () => {
    setError("");
    try {
      if (step === 0) {
        if (!property.name.trim()) return;
        setSaving(true);
        const p = await addProperty(property);
        setCreatedProperty(p);
        setSaving(false);
        setStep(1);
      } else if (step === 1) {
        setSaving(true);
        for (const t of stagedTenants) {
          await addTenant({ ...t, propertyId: createdProperty.id });
        }
        setSaving(false);
        setStep(2);
      } else if (step === 2) {
        setSaving(true);
        await finishOnboarding();
        navigate("/app/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  const addStagedTenant = () => {
    if (!tenantDraft.name.trim() || !tenantDraft.houseNumber.trim()) return;
    setStagedTenants((prev) => [...prev, { ...tenantDraft, id: Math.random() }]);
    setTenantDraft(emptyTenant());
  };

  const removeStaged = (id) =>
    setStagedTenants((prev) => prev.filter((t) => t.id !== id));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Logo size={36} />
            <span className="text-lg font-semibold tracking-tight">Paysafi</span>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Welcome{user?.fullName ? `, ${user.fullName.split(" ")[0]}` : ""}. Let's set up your first property.
          </p>
        </div>

        <Stepper step={step} />

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="card mt-6 p-6">
          {step === 0 && (
            <PropertyStep property={property} setProperty={setProperty} />
          )}
          {step === 1 && (
            <TenantsStep
              property={createdProperty}
              tenantDraft={tenantDraft}
              setTenantDraft={setTenantDraft}
              staged={stagedTenants}
              addStaged={addStagedTenant}
              removeStaged={removeStaged}
            />
          )}
          {step === 2 && (
            <SuccessStep
              property={createdProperty}
              tenantCount={stagedTenants.length}
            />
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled
          >
            Back
          </button>
          <button
            className="btn-primary"
            onClick={next}
            disabled={
              saving ||
              (step === 0 && !property.name.trim())
            }
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {step === 0 && "Create property"}
            {step === 1 && (stagedTenants.length === 0 ? "Skip for now" : `Add ${stagedTenants.length} tenant${stagedTenants.length === 1 ? "" : "s"}`)}
            {step === 2 && "Go to dashboard"}
            {step !== 2 && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function emptyTenant() {
  return {
    name: "",
    phone: "",
    houseNumber: "",
    rent: "",
    dueDay: 5,
    deposit: "",
  };
}

function Stepper({ step }) {
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                done
                  ? "bg-indigo-600 text-white"
                  : active
                  ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-600"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              }`}
            >
              {done ? "✓" : i + 1}
            </span>
            <span
              className={`text-xs font-medium ${
                active ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="ml-2 h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function PropertyStep({ property, setProperty }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create your property</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Give your building a name and address. You can add more later.
      </p>
      <div className="mt-5 space-y-4">
        <div>
          <label className="label-base">Property name</label>
          <input
            className="input-base"
            placeholder="e.g. Riverside Apartments"
            value={property.name}
            onChange={(e) => setProperty({ ...property, name: e.target.value })}
          />
        </div>
        <div>
          <label className="label-base">Address</label>
          <input
            className="input-base"
            placeholder="e.g. 14 Riverside Dr, Nairobi"
            value={property.address}
            onChange={(e) => setProperty({ ...property, address: e.target.value })}
          />
        </div>
        <div>
          <label className="label-base">Utility billing (optional)</label>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={property.waterEnabled}
              onChange={(e) => setProperty({ ...property, waterEnabled: e.target.checked })}
              className="checkbox-base"
            />
            <Droplets className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Track water billing</span>
          </label>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={property.electricityEnabled}
              onChange={(e) => setProperty({ ...property, electricityEnabled: e.target.checked })}
              className="checkbox-base"
            />
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Track electricity billing</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function TenantsStep({ property, tenantDraft, setTenantDraft, staged, addStaged, removeStaged }) {
  const set = (k) => (e) =>
    setTenantDraft({ ...tenantDraft, [k]: e.target.value });
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add tenants</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add tenants to <span className="font-medium text-gray-700 dark:text-gray-300">{property?.name}</span>. You can skip this and add them later.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label-base">Full name</label>
          <input className="input-base" value={tenantDraft.name} onChange={set("name")} />
        </div>
        <div>
          <label className="label-base">Phone</label>
          <input className="input-base" value={tenantDraft.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className="label-base">House number</label>
          <input className="input-base" placeholder="e.g. A1" value={tenantDraft.houseNumber} onChange={set("houseNumber")} />
        </div>
        <div>
          <label className="label-base">Rent (KES / month)</label>
          <input type="number" className="input-base" value={tenantDraft.rent} onChange={set("rent")} />
        </div>
        <div>
          <label className="label-base">Rent due on day</label>
          <input type="number" min="1" max="31" className="input-base" value={tenantDraft.dueDay} onChange={set("dueDay")} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-base">Deposit paid (KES)</label>
          <input type="number" className="input-base" value={tenantDraft.deposit} onChange={set("deposit")} placeholder="0" />
        </div>
      </div>
      <button className="btn-secondary mt-4" onClick={addStaged} type="button">
        <Plus className="h-4 w-4" />
        Add to list
      </button>

      {staged.length > 0 && (
        <ul className="mt-5 divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
          {staged.map((t) => (
            <li key={t.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{t.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  House {t.houseNumber} · KES {t.rent || 0} · Due {t.dueDay} of each month
                  {t.deposit ? ` · Deposit KES ${t.deposit}` : ""}
                </div>
              </div>
              <button
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600"
                onClick={() => removeStaged(t.id)}
                aria-label="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SuccessStep({ property, tenantCount }) {
  return (
    <div className="text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">You're all set</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Created <span className="font-medium text-gray-700 dark:text-gray-300">{property?.name}</span>
        {tenantCount > 0
          ? ` with ${tenantCount} tenant${tenantCount === 1 ? "" : "s"}.`
          : "."}
      </p>
      <div className="mt-6 grid grid-cols-1 gap-3 text-left sm:grid-cols-2">
        <Info icon={Building2} label="Property" value={property?.name || "—"} />
        <Info icon={Users} label="Tenants" value={tenantCount} />
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}
